import { useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import {
  removeEmptyKeysAndReplaceLimitField,
  formatQueryParams,
} from '@components/TableV2/helpers/utils';
import { ITableConfig } from '@components/TableV2/types';
import { CursorPaginationModel } from '@hooks/useCursorPagination/types';
import { useCursorPagination } from '@hooks/useCursorPagination/useCursorPagination';
import { Translate } from '@hooks/useI18n';
import { useTableSorting } from '@hooks/useTableSorting/useTableSorting';
import { useUuidsToPaymentRefsQuery } from '@pages/Contracts/queries/useUuidsToPaymentRefsQuery';
import { IUuid } from '@schemas/merchantPortal/types/merchantStores';
import {
  IMerchantReceivables,
  IOpsPortalMerchantReceivables,
} from '@schemas/opsPortal/types/merchantReceivables';
import { QueryKeys } from '@utils/constants';
import { enrichWithPaymentRefsAndExternalUuids } from '@utils/enrichWithPaymentRefsAndExternalUuids';
import { Endpoints } from '@utils/enums';
import { fetchDataFromEndpoint } from '@utils/fetchDataFromEndpoint/fetchDataFromEndpoint';
import {
  IMerchantReceivablesFilterValues,
  IOpsPortalMerchantReceivablesWithPaymentRefs,
} from './types';
import { useGetMerchantReceivablesColumns } from './useGetMerchantReceivablesColumns';
import { useGetMerchantReceivablesFilters } from './useGetMerchantReceivablesFilters';

interface IUseGetMerchantReceivablesTableConfig {
  merchantUuid: IUuid;
  translate: Translate;
}

const getUuidsList = (receivables: IMerchantReceivables) =>
  receivables.map(receivable => receivable.contract_uuid);

export const useGetMerchantReceivablesTableConfig = ({
  merchantUuid,
  translate,
}: IUseGetMerchantReceivablesTableConfig): ITableConfig<
  IMerchantReceivablesFilterValues,
  CursorPaginationModel,
  IOpsPortalMerchantReceivablesWithPaymentRefs | undefined
> => {
  const initialDefaultFilterValues: IMerchantReceivablesFilterValues = {
    createdDateFrom: '',
    createdDateTo: '',
    paid: '',
  };

  const [uuids, setUuids] = useState<string[]>([]);

  const filtersSettings = useGetMerchantReceivablesFilters({
    initialDefaultValues: initialDefaultFilterValues,
    translate,
  });

  const sortingSettings = useTableSorting();

  const paginationSettings = useCursorPagination();

  const queryParams = useMemo(
    () =>
      removeEmptyKeysAndReplaceLimitField({
        ...filtersSettings.filterValues,
        ...sortingSettings.sortSearchParam,
        ...paginationSettings.paginationParams,
      }),
    [
      filtersSettings.filterValues,
      paginationSettings.paginationParams,
      sortingSettings.sortSearchParam,
    ],
  );

  const stringifiedQueryParams = useMemo(
    () => formatQueryParams(queryParams as object),
    [queryParams],
  );

  const queryKey = useMemo(
    () => [QueryKeys.MERCHANT_RECEIVABLES, stringifiedQueryParams],
    [stringifiedQueryParams],
  );

  const columns = useGetMerchantReceivablesColumns({
    translate,
  });

  const {
    data: receivablesData,
    error: receivablesError,
    isLoading: isReceivablesLoading,
  } = useQuery<IOpsPortalMerchantReceivables, AxiosError>(
    queryKey,
    () =>
      fetchDataFromEndpoint(
        `${Endpoints.FetchMerchantReceivables}/${merchantUuid}`,
        {
          query: {
            ...(queryParams as object),
          },
        },
      ),
    {
      onSuccess: responseData => {
        paginationSettings.setNextCursor(responseData.next_cursor ?? '');
        paginationSettings.setPrevCursor(responseData.previous_cursor ?? '');
        setUuids(getUuidsList(responseData.results));
      },
    },
  );

  const {
    data: uuidPaymentRefMap,
    error: uuidPaymentRefMapError,
    isLoading: isLoadingUuidPaymentRefMap,
  } = useUuidsToPaymentRefsQuery(uuids);

  const data = useMemo(
    () =>
      enrichWithPaymentRefsAndExternalUuids(
        receivablesData ?? ({} as IOpsPortalMerchantReceivables),
        uuidPaymentRefMap?.results ?? [],
      ),
    [receivablesData, uuidPaymentRefMap],
  );

  return {
    columns,
    data,
    error: receivablesError ?? uuidPaymentRefMapError,
    filtersSettings,
    isLoading: isReceivablesLoading || isLoadingUuidPaymentRefMap,
    paginationSettings,
    sortingSettings,
  };
};
